import { store } from '@/vuex/store'
import axios from 'axios'
import tokenService from '@/services/auth/tokenService'

const empty_params = { params: {} }

// Create axios instance with interceptors
const axiosInstance = axios.create()

// Flag to prevent multiple simultaneous token refreshes
let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error)
        } else {
            prom.resolve(token)
        }
    })
    failedQueue = []
}

// Response interceptor to handle 401 errors
axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config

        // If error is 401 and we haven't already tried to refresh
        if (error.response && error.response.status === 401 && !originalRequest._retry) {
            if (isRefreshing) {
                // If already refreshing, queue this request
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject })
                }).then(token => {
                    originalRequest.headers['authorization'] = `Bearer ${token}`
                    return axiosInstance(originalRequest)
                }).catch(err => {
                    return Promise.reject(err)
                })
            }

            originalRequest._retry = true
            isRefreshing = true

            try {
                console.log('[Axios Interceptor] 401 received, refreshing token...')
                const newToken = await tokenService.refreshToken()

                if (newToken) {
                    store.state.aToken = newToken
                    originalRequest.headers['authorization'] = `Bearer ${newToken}`
                    processQueue(null, newToken)
                    return axiosInstance(originalRequest)
                }
            } catch (refreshError) {
                console.error('[Axios Interceptor] Token refresh failed:', refreshError)
                processQueue(refreshError, null)

                // Dispatch event to notify app of auth failure
                window.dispatchEvent(new CustomEvent('auth-error', {
                    detail: { error: refreshError, message: 'Session expired. Please log in again.' }
                }))

                return Promise.reject(refreshError)
            } finally {
                isRefreshing = false
            }
        }

        return Promise.reject(error)
    }
)

// Request interceptor to ensure fresh token
axiosInstance.interceptors.request.use(
    async (config) => {
        try {
            // Check if token is about to expire
            const currentToken = store.state.aToken
            if (currentToken && tokenService.isTokenExpired(currentToken, 2)) {
                console.log('[Axios Interceptor] Token expiring soon, refreshing before request...')
                const newToken = await tokenService.refreshToken()
                config.headers['authorization'] = `Bearer ${newToken}`
            }
        } catch (error) {
            console.warn('[Axios Interceptor] Pre-request token refresh failed:', error)
            // Continue with existing token, let response interceptor handle 401
        }
        return config
    },
    (error) => Promise.reject(error)
)

const setHeader = function () {
    const headers = {
        authorization: `Bearer ${store.state.aToken}`,
        locale: localStorage.getItem("lang") ? localStorage.getItem("lang") : 'hr'
    }
    return { headers }
}

const axiosGet = function (url, params = empty_params) {
    return axiosInstance.get(url, {
        params: params.params,
        headers: {
            authorization: `Bearer ${store.state.aToken}`,
            locale: localStorage.getItem("lang") ? localStorage.getItem("lang") : 'hr'
        }
    })
}

const axiosDelete = function (url, params = empty_params) {
    return axiosInstance.delete(url, {
        params: params.params,
        headers: {
            authorization: `Bearer ${store.state.aToken}`,
            locale: localStorage.getItem("lang") ? localStorage.getItem("lang") : 'hr'
        }
    })
}

const axiosPost = function (url, params = empty_params) {
    console.log(params)
    if (params.params != undefined) {
        return axiosInstance.post(url, {
            params: params.params
        }, {
            headers: {
                authorization: `Bearer ${store.state.aToken}`,
                locale: localStorage.getItem("lang") ? localStorage.getItem("lang") : 'hr'
            }
        })
    } else {
        return axiosInstance.post(url, params, {
            headers: {
                authorization: `Bearer ${store.state.aToken}`,
                locale: localStorage.getItem("lang") ? localStorage.getItem("lang") : 'hr'
            }
        })
    }
}

const axiosPut = function (url, params = empty_params) {
    if (params.params != undefined) {
        return axiosInstance.put(url, {
            params: params.params
        }, {
            headers: {
                authorization: `Bearer ${store.state.aToken}`,
                locale: localStorage.getItem("lang") ? localStorage.getItem("lang") : 'hr'
            }
        })
    } else {
        return axiosInstance.put(url, params, {
            headers: {
                authorization: `Bearer ${store.state.aToken}`,
                locale: localStorage.getItem("lang") ? localStorage.getItem("lang") : 'hr'
            }
        })
    }
}

export {
    setHeader, axiosGet, axiosDelete, axiosPut, axiosPost, axiosInstance
}
